import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import type { QueryRecord } from '@cdr-clarity/contracts';
import type { RelatedPreview as Preview } from '../state/related-records.js';
import { cellHelp, cellText, fieldTitle } from '../presentation/cells.js';
import { FreezeIcon } from './FreezeIcon.js';

export interface RelatedPreviewProps {
  preview: Preview;
  sourceId: string;
  timezone: string;
  pinned: boolean;
  busy: boolean;
  page: number;
  close(): void;
  togglePin(): void;
  focus(ids: readonly string[]): void;
  inspect(record: QueryRecord, collection: 'cdr' | 'cmr'): void;
  previous(): void;
  next(): void;
}

/** The retained preview presents source records and service evidence without joining them. */
export function RelatedPreview(props: RelatedPreviewProps) {
  const { preview, sourceId, timezone, busy } = props;
  const { result, collections } = preview;
  const visibleFields = collections.flatMap((collection) => collection.fields);
  return (
    <section
      className="related-preview"
      data-testid="related-preview"
      data-total={result.total}
      role="dialog"
      aria-label={viewerStrings.webComponentsRelatedPreview.relatedRecords}
      aria-busy={busy}
    >
      <header>
        <div data-copy-group>
          {hasText(viewerStrings.webComponentsRelatedPreview.established) ? (
            <strong>
              {message(viewerStrings.webComponentsRelatedPreview.established, {
                value1: result.total,
                value2:
                  result.total === 1
                    ? viewerStrings.webRelated.relationship
                    : viewerStrings.webRelated.relationships,
              })}
            </strong>
          ) : null}
          {hasText(
            viewerStrings.webComponentsRelatedPreview.importedRecordsOnlyCurrentFiltersAndHidden,
          ) ? (
            <small>
              {viewerStrings.webComponentsRelatedPreview.importedRecordsOnlyCurrentFiltersAndHidden}
            </small>
          ) : null}
        </div>
        {hasText(viewerStrings.webComponentsRelatedPreview.filterTo) ? (
          <button
            data-testid="related-focus"
            className="primary"
            onClick={() => props.focus(result.recordIds)}
          >
            {message(viewerStrings.webComponentsRelatedPreview.filterTo, {
              value1: result.recordIds.length,
              value2:
                result.recordIds.length === 1
                  ? viewerStrings.webRelated.record
                  : viewerStrings.webRelated.records,
            })}
          </button>
        ) : null}
        <FreezeIcon
          active={props.pinned}
          label={
            props.pinned
              ? viewerStrings.webComponentsRelatedPreview.unpinPopup
              : viewerStrings.webComponentsRelatedPreview.pinPopup
          }
          onClick={props.togglePin}
          accessibleLabel={
            props.pinned
              ? viewerStrings.webIconAccessibility.webComponentsRelatedPreviewunpinPopup
              : viewerStrings.webIconAccessibility.webComponentsRelatedPreviewpinPopup
          }
        />
        <button
          className="popup-close"
          aria-label={viewerStrings.webComponentsRelatedPreview.closeRelatedRecords}
          onClick={props.close}
        >
          ×
        </button>
      </header>
      {!result.total &&
        (hasText(
          viewerStrings.webComponentsRelatedPreview.noEstablishedRelationshipsInTheImportedRecords,
        ) ? (
          <p>
            {
              viewerStrings.webComponentsRelatedPreview
                .noEstablishedRelationshipsInTheImportedRecords
            }
          </p>
        ) : null)}
      <ul className="related-evidence">
        {result.relationships.map((edge, index) => (
          <li key={index}>
            <strong>
              {edge.type.replaceAll('-', ' ')} · {edge.direction.replaceAll('-', ' ')}
            </strong>
            {hasText(
              edge.status === 'unresolved'
                ? viewerStrings.webComponentsRelatedPreview.targetUnavailableInImportedRecords
                : viewerStrings.webComponentsRelatedPreview.resolvedInImportedRecords,
            ) ? (
              <span>
                {edge.status === 'unresolved'
                  ? viewerStrings.webComponentsRelatedPreview.targetUnavailableInImportedRecords
                  : viewerStrings.webComponentsRelatedPreview.resolvedInImportedRecords}
              </span>
            ) : null}
            {hasText(
              edge.supportingFields
                .flatMap((id) => {
                  const field = visibleFields.find((candidate) => candidate.id === id);
                  return field ? [field.title] : [];
                })
                .join(' · ') || viewerStrings.webRelated.hiddenSupportingFields,
            ) ? (
              <small>
                {edge.supportingFields
                  .flatMap((id) => {
                    const field = visibleFields.find((candidate) => candidate.id === id);
                    return field ? [field.title] : [];
                  })
                  .join(' · ') || viewerStrings.webRelated.hiddenSupportingFields}
              </small>
            ) : null}
            <small>{edge.citation}</small>
          </li>
        ))}
      </ul>
      {collections
        .filter((collection) => collection.records.length)
        .map((collection) => (
          <div className="related-record-collection" key={collection.collection}>
            {hasText(
              collection.collection === 'cdr'
                ? viewerStrings.webComponentsRelatedPreview.callSourceRecords
                : viewerStrings.webComponentsRelatedPreview.mediaSourceRecords,
            ) ? (
              <h3>
                {collection.collection === 'cdr'
                  ? viewerStrings.webComponentsRelatedPreview.callSourceRecords
                  : viewerStrings.webComponentsRelatedPreview.mediaSourceRecords}
              </h3>
            ) : null}
            <div className="related-preview-table">
              <table>
                <thead>
                  <tr>
                    <th>{viewerStrings.webComponentsRelatedPreview.record}</th>
                    {collection.fields.slice(0, 6).map((field) => (
                      <th key={field.id}>{fieldTitle(field, timezone)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {collection.records.map((record) => (
                    <tr key={record.id} className={record.id === sourceId ? 'related-source' : ''}>
                      <td>
                        <button
                          aria-label={
                            viewerStrings.webComponentsRelatedPreview.inspectRelatedSourceRecord
                          }
                          title={optionalText(record.id)}
                          onClick={() => props.inspect(record, collection.collection)}
                        >
                          {record.id === sourceId
                            ? viewerStrings.webComponentsRelatedPreview.source
                            : collection.collection.toUpperCase()}{' '}
                          ↗
                        </button>
                      </td>
                      {collection.fields.slice(0, 6).map((field) => (
                        <td
                          key={field.id}
                          title={optionalText(cellHelp(record.cells[field.id], field, timezone))}
                        >
                          {cellText(record.cells[field.id], field, timezone)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      <footer data-copy-group>
        {hasText(viewerStrings.webComponentsRelatedPreview.relationshipPageSourcePlusThisPageS) ? (
          <span>
            {message(
              viewerStrings.webComponentsRelatedPreview.relationshipPageSourcePlusThisPageS,
              { value1: props.page },
            )}
          </span>
        ) : null}
        {hasText(viewerStrings.webComponentsRelatedPreview.previous) ? (
          <button disabled={busy || props.page === 1} onClick={props.previous}>
            {viewerStrings.webComponentsRelatedPreview.previous}
          </button>
        ) : null}
        {hasText(viewerStrings.webComponentsRelatedPreview.next) ? (
          <button disabled={busy || !result.nextCursor} onClick={props.next}>
            {viewerStrings.webComponentsRelatedPreview.next}
          </button>
        ) : null}
      </footer>
    </section>
  );
}
